import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUp, ExternalLink, Loader2, Lock, Search, Share2, X } from "lucide-react";
import {
  absoluteUploadUrl,
  getPublicPage,
  searchPublicProducts,
  trackProductClick,
  type CustomBlock,
  type PublicPage,
  type PublicProduct,
  type SearchHit,
} from "../lib/page";
import { getThemeColors } from "../lib/theme_presets";
import { renderMarkdown } from "../lib/markdown";
import { WishlistHeart } from "../components/Wishlist";
import { RecentlyViewedStrip } from "../components/Discovery";
import { DeliveryAnnouncementBar, ShopHeader } from "../components/ShopHeader";
import { ShopTabBar } from "../components/ShopTabBar";
import {
  DefaultBrandHero,
  HeroSlider,
  PromoStrip,
  TrendingSection,
} from "../components/ShopBlocks";
import { navigate } from "../lib/router";

// URL-State Helpers fuer Kategorie-Deep-Links (?cat=Liquids)
function readCatFromUrl(): string | null {
  try {
    const sp = new URLSearchParams(window.location.search);
    const c = sp.get("cat");
    return c && c.length > 0 ? c : null;
  } catch {
    return null;
  }
}

function writeCatToUrl(cat: string | null): void {
  try {
    const sp = new URLSearchParams(window.location.search);
    if (cat) {
      sp.set("cat", cat);
    } else {
      sp.delete("cat");
    }
    const next = sp.toString();
    const url = window.location.pathname + (next ? `?${next}` : "") + window.location.hash;
    window.history.replaceState(window.history.state, "", url);
  } catch {
    /* ignore */
  }
}

type Props = {
  agentCode: string;
};

export function PublicLandingPage({ agentCode }: Props) {
  const { data, isLoading, error } = useQuery<PublicPage>({
    queryKey: ["public-page", agentCode],
    queryFn: () => getPublicPage(agentCode),
    retry: false,
    refetchInterval: 5 * 60_000,
  });

  useEffect(() => {
    if (data?.headline) {
      document.title = data.headline;
    } else if (data) {
      document.title = `${data.agent_first_name} ${data.agent_last_name} — DP Connect`;
    }
  }, [data]);

  // WICHTIG: Alle Hooks MÜSSEN vor jedem Early-Return aufgerufen werden
  // (React Rules-of-Hooks). Deshalb berechnen wir Memos defensiv mit Fallbacks.
  const categoryList = useMemo(() => {
    if (!data?.products) return [];
    const counts = new Map<string, number>();
    for (const p of data.products) {
      for (const c of p.categories || []) {
        if (!c) continue;
        counts.set(c, (counts.get(c) || 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "de"));
  }, [data?.products]);

  const [activeCat, setActiveCat] = useState<string | null>(() => readCatFromUrl());
  useEffect(() => {
    writeCatToUrl(activeCat);
  }, [activeCat]);

  if (isLoading) {
    return (
      <main className="min-h-screen grid place-items-center bg-cream">
        <div className="text-sm text-ink-muted">Lade…</div>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="min-h-screen grid place-items-center bg-cream px-4">
        <div className="text-center">
          <div className="text-4xl mb-2">🤷</div>
          <h1 className="text-xl font-semibold tracking-tight">Diese Seite gibt es nicht</h1>
          <p className="mt-2 text-sm text-ink-muted">
            Vielleicht ist sie noch nicht veröffentlicht oder der Link stimmt nicht.
          </p>
          <a
            href="https://dpconnect.de"
            className="mt-4 inline-block text-sm text-accent-orange hover:underline"
          >
            Zum DP-Connect-Shop →
          </a>
        </div>
      </main>
    );
  }

  // brand_primary überschreibt accent_color als stärkstes Branding-Signal
  const effectiveAccent = data.brand_primary || data.accent_color;
  const theme = getThemeColors(data.theme_preset, effectiveAccent);
  const isDark = data.theme_preset === "midnight";
  const initials = `${data.agent_first_name[0] || ""}${data.agent_last_name[0] || ""}`.toUpperCase();
  const fullName = `${data.agent_first_name} ${data.agent_last_name}`.trim();
  const avatarUrl = absoluteUploadUrl(data.avatar_url);
  const coverUrl = absoluteUploadUrl(data.cover_url);
  const logoUrl = absoluteUploadUrl(data.logo_url);

  const handleClick = (productRowId: string) => {
    trackProductClick(data.agent_code, productRowId);
  };

  const featured = data.products.find((p) => p.is_featured) || null;
  const rest = featured ? data.products.filter((p) => p.id !== featured.id) : data.products;

  return (
    <main style={{ background: theme.bg, color: theme.ink }} className="min-h-screen">
      {/* Top-Bar mit Liefer-Promise (wie auf PDP) */}
      <DeliveryAnnouncementBar isDark={isDark} hasWhatsApp={!!data.whatsapp_number} />

      {/* Always-on Sticky-Header — Logo + Nav + Cart/Wishlist/Profile */}
      <ShopHeader
        page={data}
        agentCode={data.agent_code}
        theme={theme}
        isDark={isDark}
        categoryList={categoryList}
        activeCat={activeCat}
        onSelectCat={setActiveCat}
      />

      {/* Hero-Dispatch: Slider > Cover > Default-Gradient */}
      {(data.hero_slides && data.hero_slides.length > 0) ? (
        <HeroSlider slides={data.hero_slides} theme={theme} isDark={isDark} />
      ) : !coverUrl && !logoUrl && !avatarUrl ? (
        <DefaultBrandHero
          brandName={data.headline || fullName}
          initials={initials}
          theme={theme}
          agentCode={data.agent_code}
        />
      ) : null}

      {/* Brand-Strip + Cover (fallback) */}
      <ShopHero
        data={data}
        coverUrl={
          // Wenn Slider aktiv ist, NICHT noch zusätzlich den statischen Cover anzeigen
          data.hero_slides && data.hero_slides.length > 0 ? null : coverUrl
        }
        logoUrl={logoUrl}
        avatarUrl={avatarUrl}
        initials={initials}
        fullName={fullName}
        theme={theme}
        isDark={isDark}
      />

      {/* Promo-Strip — Discount-Codes aus Page-Products + manuelle promo_strip */}
      <PromoStrip
        pills={data.promo_strip || []}
        productPromos={data.products.filter((p) => p.discount_code)}
        theme={theme}
        isDark={isDark}
      />

      {/* Trending — Top-Seller */}
      <TrendingSection
        products={data.products}
        agentCode={data.agent_code}
        theme={theme}
        isDark={isDark}
        isB2bLocked={data.is_b2b_locked}
      />

      {/* B2B-Lock Banner */}
      {data.is_b2b_locked && data.register_url && (
        <B2BBanner registerUrl={data.register_url} theme={theme} isDark={isDark} />
      )}

      {/* Custom blocks zwischen Bio und Produkten */}
      <CustomBlocksRenderer
        blocks={data.custom_blocks || []}
        products={data.products}
        theme={theme}
        isDark={isDark}
        onClick={handleClick}
      />

      {/* Featured (curated only) */}
      {featured && (
        <FeaturedHero
          product={featured}
          theme={theme}
          isDark={isDark}
          onClick={handleClick}
          isB2bLocked={data.is_b2b_locked}
        />
      )}

      {/* Products */}
      {data.page_mode === "full_shop" ? (
        <FullShopSection
          data={data}
          theme={theme}
          isDark={isDark}
          onClick={handleClick}
          activeCat={activeCat}
          setActiveCat={setActiveCat}
        />
      ) : (
        <CuratedSection
          products={
            activeCat
              ? rest.filter((p) => (p.categories || []).includes(activeCat))
              : rest
          }
          theme={theme}
          isDark={isDark}
          onClick={handleClick}
          isB2bLocked={data.is_b2b_locked}
          activeCat={activeCat}
          onClearCat={() => setActiveCat(null)}
          agentCode={data.agent_code}
        />
      )}

      {/* Recently-Viewed direkt vor dem Footer */}
      {!data.is_b2b_locked && (
        <RecentlyViewedStrip agentCode={data.agent_code} theme={theme} isDark={isDark} />
      )}

      <PageFooter data={data} theme={theme} isDark={isDark} fullName={fullName} logoUrl={logoUrl} />

      {/* Scroll-to-Top FAB nach 400px Scroll */}
      <ScrollToTopButton theme={theme} />

      {/* Sticky Register-Banner unten wenn B2B-locked + Anonymous-Visitor */}
      {data.is_b2b_locked && data.register_url && (
        <StickyB2BBanner registerUrl={data.register_url} theme={theme} />
      )}

      {/* Mobile-Tab-Bar — fixed unten auf Mobile */}
      <ShopTabBar agentCode={data.agent_code} theme={theme} isDark={isDark} />
    </main>
  );
}

// ============================================================
// ShopHero — kompakter Brand-Hero (Cover-Banner mit Brand-Strip)
// ============================================================

function ShopHero({
  data,
  coverUrl,
  logoUrl,
  avatarUrl,
  initials,
  fullName,
  theme,
  isDark,
}: {
  data: PublicPage;
  coverUrl: string | null;
  logoUrl: string | null;
  avatarUrl: string | null;
  initials: string;
  fullName: string;
  theme: ThemeT;
  isDark: boolean;
}) {
  return (
    <>
      {/* Cover-Banner — kein dominanter Avatar, nur Brand-Identity unten */}
      {coverUrl ? (
        <div className="relative w-full h-40 md:h-56 lg:h-64 overflow-hidden">
          <img
            src={coverUrl}
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
          {/* Gradient für Lesbarkeit der Brand-Pill unten */}
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 30%, ${theme.bg} 100%)`,
            }}
          />
        </div>
      ) : (
        <div
          className="w-full"
          style={{
            background: `linear-gradient(135deg, ${theme.gradientFrom}15, ${theme.gradientTo}15)`,
            height: "40px",
          }}
        />
      )}

      {/* Live-Suche prominent direkt unter Cover */}
      <div className="mx-auto max-w-3xl px-4 -mt-6 md:-mt-8 relative z-10 mb-3">
        <LiveSearchBar
          agentCode={data.agent_code}
          theme={theme}
          isDark={isDark}
          isB2bLocked={data.is_b2b_locked}
        />
      </div>

      {/* Brand-Strip: Avatar + Name + WhatsApp + Social — kompakt, horizontal */}
      <div className="mx-auto max-w-6xl px-4 pb-4 md:pb-5">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Avatar/Initials klein, links */}
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={fullName}
              className="size-12 md:size-14 rounded-full object-cover flex-shrink-0"
              style={{
                boxShadow: `0 0 0 3px ${theme.bg}, 0 4px 12px rgba(0,0,0,0.08)`,
              }}
              loading="eager"
            />
          ) : !logoUrl ? (
            <div
              className="size-12 md:size-14 rounded-full grid place-items-center text-white text-sm md:text-base font-bold flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, ${theme.gradientFrom}, ${theme.gradientTo})`,
              }}
            >
              {initials || "DP"}
            </div>
          ) : null}

          <div className="min-w-0 flex-1">
            <div
              className="text-xs uppercase tracking-wider font-semibold"
              style={{ color: theme.inkMuted }}
            >
              Dein Fachhandel
            </div>
            <div
              className="text-base md:text-lg font-bold leading-tight"
              style={{ color: theme.ink }}
            >
              {data.headline || fullName}
            </div>
          </div>

          {/* WhatsApp-CTA prominent */}
          {data.whatsapp_number && (
            <a
              href={buildWhatsAppLink(data.whatsapp_number, data.whatsapp_message)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl px-3 md:px-4 py-2 text-xs md:text-sm font-semibold shadow-md transition-transform hover:scale-105"
              style={{ background: "#25d366", color: "#fff" }}
            >
              <svg
                viewBox="0 0 24 24"
                className="size-4"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M20.5 3.5A11.4 11.4 0 0 0 12.1 0C5.6 0 .4 5.2.4 11.7c0 2 .5 4 1.6 5.7L0 24l6.8-1.8a11.6 11.6 0 0 0 5.3 1.4h.1c6.5 0 11.7-5.2 11.7-11.7 0-3.1-1.2-6-3.4-8.4Z" />
              </svg>
              <span className="hidden sm:inline">WhatsApp</span>
            </a>
          )}

          {/* Social-Links subtle */}
          {(data.instagram_url || data.tiktok_url) && (
            <div className="flex items-center gap-1">
              {data.instagram_url && (
                <a
                  href={data.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="grid place-items-center size-8 rounded-lg border hover:opacity-100 opacity-70"
                  style={{
                    color: theme.inkMuted,
                    borderColor: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.10)",
                  }}
                  title="Instagram"
                >
                  <svg
                    className="size-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                  </svg>
                </a>
              )}
              {data.tiktok_url && (
                <a
                  href={data.tiktok_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="grid place-items-center size-8 rounded-lg border text-[10px] font-bold hover:opacity-100 opacity-70"
                  style={{
                    color: theme.inkMuted,
                    borderColor: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.10)",
                  }}
                  title="TikTok"
                >
                  TT
                </a>
              )}
              <ShareButton pageData={data} theme={theme} isDark={isDark} />
            </div>
          )}
        </div>

        {/* Optional Subtitle/Bio — klein und collapsible-feel */}
        {data.subtitle && (
          <div
            className="mt-3 md:mt-4 text-xs md:text-sm leading-relaxed max-w-2xl"
            style={{ color: theme.inkMuted }}
          >
            {renderMarkdown(data.subtitle)}
          </div>
        )}
      </div>
    </>
  );
}

function ScrollToTopButton({ theme }: { theme: ThemeT }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed right-4 z-40 size-11 grid place-items-center rounded-full shadow-lg transition-opacity hover:scale-105 active:scale-95"
      style={{
        background: theme.cardBg,
        color: theme.accent,
        border: `1px solid ${theme.accent}40`,
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px + 80px)",
      }}
      aria-label="Nach oben"
      title="Nach oben"
    >
      <ArrowUp className="size-5" strokeWidth={2.5} />
    </button>
  );
}

function StickyB2BBanner({
  registerUrl,
  theme,
}: {
  registerUrl: string;
  theme: ThemeT;
}) {
  const [visible, setVisible] = useState(false);
  // Erst nach 200px Scroll einblenden — sonst doppelt mit dem Hero-Banner
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 250);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      className={`fixed left-0 right-0 z-30 transition-transform duration-300 ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
      style={{
        bottom: 0,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        background: theme.cardBg,
        boxShadow: "0 -8px 24px rgba(0,0,0,0.08)",
        borderTop: `1px solid ${theme.accent}30`,
      }}
    >
      <div className="mx-auto max-w-3xl px-4 py-3 flex items-center gap-3">
        <Lock className="size-5 flex-shrink-0" style={{ color: theme.accent }} />
        <div className="text-xs leading-tight flex-1 min-w-0">
          <div className="font-semibold" style={{ color: theme.ink }}>
            Preise nach Login
          </div>
          <div className="text-[11px]" style={{ color: theme.inkMuted }}>
            B2B-Großhandel — kurz registrieren
          </div>
        </div>
        <a
          href={registerUrl}
          className="inline-flex items-center rounded-xl px-4 py-2 text-sm font-semibold whitespace-nowrap"
          style={{ background: theme.accent, color: "#fff" }}
        >
          Registrieren →
        </a>
      </div>
    </div>
  );
}

type ThemeT = ReturnType<typeof getThemeColors>;

function buildWhatsAppLink(number: string, message: string | null): string {
  const digits = number.replace(/[^0-9]/g, "");
  const text = message ? `?text=${encodeURIComponent(message)}` : "";
  return `https://wa.me/${digits}${text}`;
}

// ----------------------------------------------------------------------------
// B2B-Banner: zeigt den Hinweis dass es ein B2B-Großhandel ist
// ----------------------------------------------------------------------------

function B2BBanner({
  registerUrl,
  theme,
  isDark,
}: {
  registerUrl: string;
  theme: ThemeT;
  isDark: boolean;
}) {
  return (
    <section className="mx-auto max-w-3xl px-4 pt-4">
      <div
        className="rounded-2xl border-2 p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3"
        style={{
          background: isDark ? "rgba(255,255,255,.05)" : `${theme.accent}10`,
          borderColor: theme.accent,
        }}
      >
        <div className="flex items-start gap-2 flex-1">
          <Lock className="size-5 flex-shrink-0 mt-0.5" style={{ color: theme.accent }} />
          <div className="min-w-0">
            <div className="font-semibold text-sm" style={{ color: theme.ink }}>
              B2B-Großhandel — nur für Gewerbetreibende
            </div>
            <p className="mt-0.5 text-xs leading-snug" style={{ color: theme.inkMuted }}>
              Großhandelspreise werden nach kostenloser Registrierung freigeschaltet.
            </p>
          </div>
        </div>
        <a
          href={registerUrl}
          className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold whitespace-nowrap"
          style={{ background: theme.accent, color: "#fff" }}
        >
          Jetzt registrieren →
        </a>
      </div>
      <p className="mt-1.5 text-[11px] text-center" style={{ color: theme.inkMuted }}>
        Schon Kunde?{" "}
        <a
          href="https://dpconnect.de/mein-konto/"
          className="underline hover:opacity-80"
          style={{ color: theme.accent }}
        >
          Hier einloggen
        </a>
      </p>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Web-Share-API
// ----------------------------------------------------------------------------

function ShareButton({
  pageData,
  theme,
  isDark,
}: {
  pageData: PublicPage;
  theme: ThemeT;
  isDark: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const title = pageData.headline || `${pageData.agent_first_name} bei DP Connect`;

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ url, title, text: pageData.subtitle || undefined });
        return;
      } catch {
        /* user cancelled */
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  };

  return (
    <button
      onClick={share}
      className={`inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 border ${
        isDark ? "border-white/10" : "border-black/10"
      }`}
      style={{ background: theme.cardBg, color: theme.inkMuted }}
    >
      <Share2 className="size-3" />
      {copied ? "Link kopiert!" : "Teilen"}
    </button>
  );
}

// ----------------------------------------------------------------------------
// Custom Blocks Renderer
// ----------------------------------------------------------------------------

function CustomBlocksRenderer({
  blocks,
  products,
  theme,
  isDark,
  onClick,
}: {
  blocks: CustomBlock[];
  products: PublicProduct[];
  theme: ThemeT;
  isDark: boolean;
  onClick: (id: string) => void;
}) {
  if (!blocks || blocks.length === 0) return null;
  return (
    <section className="mx-auto max-w-3xl px-4 pb-6 space-y-3">
      {blocks.map((b, idx) => {
        if (b.type === "text" && b.props.markdown) {
          return (
            <div
              key={idx}
              className="rounded-2xl p-4 leading-relaxed text-sm"
              style={{ background: theme.cardBg, color: theme.ink }}
            >
              {renderMarkdown(b.props.markdown)}
            </div>
          );
        }
        if (b.type === "video" && b.props.url) {
          return <VideoEmbed key={idx} url={b.props.url} caption={b.props.caption} theme={theme} />;
        }
        if (b.type === "spacer") {
          const h = b.props.size === "sm" ? "h-3" : b.props.size === "lg" ? "h-12" : "h-6";
          return <div key={idx} className={h} />;
        }
        if (b.type === "bundle") {
          return (
            <BundleBlock
              key={idx}
              bundle={b.props}
              products={products}
              theme={theme}
              isDark={isDark}
              onClick={onClick}
            />
          );
        }
        if (b.type === "faq") {
          return <FaqBlock key={idx} props={b.props} theme={theme} isDark={isDark} />;
        }
        if (b.type === "testimonial") {
          return <TestimonialBlock key={idx} props={b.props} theme={theme} isDark={isDark} />;
        }
        if (b.type === "image") {
          return <ImageBlock key={idx} props={b.props} theme={theme} />;
        }
        if (b.type === "section_header") {
          return <SectionHeaderBlock key={idx} props={b.props} theme={theme} />;
        }
        return null;
      })}
      {/* nur visuelle Trennung wenn anschließend Produkte folgen */}
      <div className="h-px" style={{ background: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)" }} />
    </section>
  );
}

function BundleBlock({
  bundle,
  products,
  theme,
  isDark,
  onClick,
}: {
  bundle: { title: string; product_ids: string[]; cta_label?: string };
  products: PublicProduct[];
  theme: ThemeT;
  isDark: boolean;
  onClick: (id: string) => void;
}) {
  const matched = bundle.product_ids
    .map((id) => products.find((p) => p.id === id))
    .filter((p): p is PublicProduct => !!p);
  if (matched.length === 0) return null;

  const totalNum = matched.reduce((sum, p) => sum + (parseFloat(p.price || "0") || 0), 0);
  const totalText = totalNum > 0 ? `${totalNum.toFixed(2).replace(".", ",")} €` : null;

  // First product as primary CTA, rest as small previews
  const primary = matched[0];
  return (
    <div
      className={`rounded-2xl p-4 border-2 ${isDark ? "border-white/10" : "border-black/5"}`}
      style={{ background: theme.cardBg }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
          style={{ background: theme.accent, color: "#fff" }}
        >
          📦 Bundle
        </span>
        <h3 className="text-base font-semibold" style={{ color: theme.ink }}>
          {bundle.title}
        </h3>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {matched.slice(0, 6).map((p) => (
          <a
            key={p.id}
            href={p.permalink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onClick(p.id)}
            className="block"
          >
            {p.image_url ? (
              <img
                src={p.image_url}
                alt={p.name}
                className="w-full aspect-square object-cover rounded-lg"
                loading="lazy"
              />
            ) : (
              <div
                className="w-full aspect-square rounded-lg"
                style={{ background: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.04)" }}
              />
            )}
            <div
              className="mt-1 text-[10px] truncate"
              style={{ color: theme.inkMuted }}
            >
              {p.name}
            </div>
          </a>
        ))}
      </div>
      {totalText && (
        <div className="text-xs mb-2" style={{ color: theme.inkMuted }}>
          Gesamt: <strong style={{ color: theme.accent }}>{totalText}</strong>
        </div>
      )}
      <a
        href={primary.permalink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => onClick(primary.id)}
        className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold w-full"
        style={{ background: theme.accent, color: "#fff" }}
      >
        {bundle.cta_label || "Alle ansehen"} →
      </a>
    </div>
  );
}

function FaqBlock({
  props,
  theme,
  isDark,
}: {
  props: { title?: string; items: { q: string; a: string }[] };
  theme: ThemeT;
  isDark: boolean;
}) {
  const items = (props.items || []).filter((x) => x.q.trim() && x.a.trim());
  if (items.length === 0) return null;
  return (
    <div
      className="rounded-2xl p-4"
      style={{ background: theme.cardBg, color: theme.ink }}
    >
      <h3 className="text-base font-semibold mb-2" style={{ color: theme.ink }}>
        {props.title || "Häufige Fragen"}
      </h3>
      <div className="space-y-1">
        {items.map((it, i) => (
          <FaqItemView key={i} q={it.q} a={it.a} theme={theme} isDark={isDark} />
        ))}
      </div>
    </div>
  );
}

function FaqItemView({
  q,
  a,
  theme,
  isDark,
}: {
  q: string;
  a: string;
  theme: ThemeT;
  isDark: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`border-t first:border-t-0 ${isDark ? "border-white/10" : "border-black/05"}`}
      style={{ borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)" }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left py-2.5 flex items-center gap-2"
      >
        <span
          className="size-5 grid place-items-center rounded-full text-xs font-bold transition-transform"
          style={{
            background: theme.accent + "22",
            color: theme.accent,
            transform: open ? "rotate(45deg)" : "none",
          }}
        >
          +
        </span>
        <span className="text-sm font-medium flex-1" style={{ color: theme.ink }}>
          {q}
        </span>
      </button>
      {open && (
        <div
          className="pb-3 pl-7 text-sm leading-relaxed"
          style={{ color: theme.inkMuted }}
        >
          {renderMarkdown(a)}
        </div>
      )}
    </div>
  );
}

function TestimonialBlock({
  props,
  theme,
}: {
  props: { quote: string; author: string; role?: string; image_url?: string };
  theme: ThemeT;
  isDark: boolean;
}) {
  if (!props.quote.trim()) return null;
  const img = absoluteUploadUrl(props.image_url || null);
  return (
    <div
      className="rounded-2xl p-5 relative"
      style={{ background: theme.cardBg }}
    >
      <span
        className="absolute -top-3 left-4 text-5xl leading-none select-none opacity-30"
        style={{ color: theme.accent }}
      >
        “
      </span>
      <p
        className="text-base leading-relaxed italic mb-4"
        style={{ color: theme.ink }}
      >
        {props.quote}
      </p>
      <div className="flex items-center gap-3">
        {img ? (
          <img
            src={img}
            alt={props.author}
            className="size-10 rounded-full object-cover"
            loading="lazy"
          />
        ) : (
          <div
            className="size-10 rounded-full grid place-items-center text-white font-bold"
            style={{ background: theme.accent }}
          >
            {props.author.slice(0, 1).toUpperCase()}
          </div>
        )}
        <div>
          <div className="text-sm font-semibold" style={{ color: theme.ink }}>
            {props.author}
          </div>
          {props.role && (
            <div className="text-xs" style={{ color: theme.inkMuted }}>
              {props.role}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ImageBlock({
  props,
  theme,
}: {
  props: { url: string; alt?: string; caption?: string; link_url?: string };
  theme: ThemeT;
}) {
  const url = absoluteUploadUrl(props.url);
  if (!url) return null;
  const img = (
    <img
      src={url}
      alt={props.alt || ""}
      className="w-full rounded-2xl object-cover"
      loading="lazy"
    />
  );
  return (
    <div>
      {props.link_url ? (
        <a href={props.link_url} target="_blank" rel="noopener noreferrer">
          {img}
        </a>
      ) : (
        img
      )}
      {props.caption && (
        <p
          className="mt-1.5 text-xs italic text-center px-2"
          style={{ color: theme.inkMuted }}
        >
          {props.caption}
        </p>
      )}
    </div>
  );
}

function SectionHeaderBlock({
  props,
  theme,
}: {
  props: { title: string; emoji?: string; subtitle?: string };
  theme: ThemeT;
}) {
  if (!props.title?.trim()) return null;
  return (
    <div className="pt-2 px-1">
      <div className="flex items-center gap-2">
        {props.emoji && <span className="text-2xl">{props.emoji}</span>}
        <h2
          className="text-xl font-bold tracking-tight"
          style={{ color: theme.ink }}
        >
          {props.title}
        </h2>
        <span
          className="flex-1 h-px"
          style={{
            background: `linear-gradient(to right, ${theme.accent}40, transparent)`,
          }}
        />
      </div>
      {props.subtitle && (
        <p
          className="mt-1 text-sm pl-1"
          style={{ color: theme.inkMuted }}
        >
          {props.subtitle}
        </p>
      )}
    </div>
  );
}

function VideoEmbed({
  url,
  caption,
  theme,
}: {
  url: string;
  caption?: string;
  theme: ThemeT;
}) {
  const embed = toEmbedUrl(url);
  if (!embed) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-2xl p-3 text-sm text-center underline"
        style={{ background: theme.cardBg, color: theme.accent }}
      >
        Video anschauen →
      </a>
    );
  }
  const aspectClass = embed.kind === "tiktok" || embed.kind === "instagram"
    ? "aspect-[9/16]"
    : "aspect-video";
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: theme.cardBg }}>
      <div className={`relative ${aspectClass} w-full`}>
        <iframe
          src={embed.src}
          title={caption || "Video"}
          className="absolute inset-0 w-full h-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      </div>
      {caption && (
        <p className="px-3 py-2 text-xs italic" style={{ color: theme.inkMuted }}>
          {caption}
        </p>
      )}
    </div>
  );
}

function toEmbedUrl(url: string): { src: string; kind: string } | null {
  // YouTube
  const yt = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
  );
  if (yt) return { src: `https://www.youtube.com/embed/${yt[1]}`, kind: "youtube" };
  // Vimeo
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return { src: `https://player.vimeo.com/video/${vm[1]}`, kind: "vimeo" };
  // TikTok
  const tt = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
  if (tt) return { src: `https://www.tiktok.com/embed/v2/${tt[1]}`, kind: "tiktok" };
  // Instagram (Reel / Post)
  const ig = url.match(/instagram\.com\/(?:reel|p)\/([A-Za-z0-9_-]+)/);
  if (ig) return { src: `https://www.instagram.com/p/${ig[1]}/embed/`, kind: "instagram" };
  return null;
}

// ----------------------------------------------------------------------------
// Featured/Hero-Produkt
// ----------------------------------------------------------------------------

function FeaturedHero({
  product: p,
  theme,
  isDark,
  onClick,
  isB2bLocked,
}: {
  product: PublicProduct;
  theme: ThemeT;
  isDark: boolean;
  onClick: (id: string) => void;
  isB2bLocked: boolean;
}) {
  const pdpHref = !isB2bLocked && p.wc_product_id ? `/p/${p.wc_product_id}` : p.permalink;
  const opensExternal = !pdpHref.startsWith("/");
  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClick(p.id);
    if (!opensExternal && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      navigate(pdpHref);
    }
  };
  return (
    <section className="mx-auto max-w-6xl px-4 py-3 md:py-6">
      <a
        href={pdpHref}
        target={opensExternal ? "_blank" : undefined}
        rel={opensExternal ? "noopener noreferrer" : undefined}
        onClick={handleNav}
        onAuxClick={() => onClick(p.id)}
        className={`block rounded-3xl overflow-hidden transition-all group border-2 ${
          isDark ? "border-white/10 hover:border-white/30" : "border-black/5 hover:border-black/20"
        }`}
        style={{
          background: theme.cardBg,
          boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          {p.image_url ? (
            <img
              src={p.image_url}
              alt=""
              className="w-full aspect-square md:aspect-auto md:h-full md:min-h-[420px] object-cover"
              loading="eager"
            />
          ) : (
            <div
              className="aspect-square md:min-h-[420px]"
              style={{ background: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.04)" }}
            />
          )}
          <div className="p-6 md:p-10 lg:p-12 flex flex-col justify-center">
            <div className="flex items-center gap-1.5 mb-3 flex-wrap">
              <span
                className="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider"
                style={{ background: theme.accent, color: "#fff" }}
              >
                ★ Mein Top-Pick
              </span>
              <StockPills p={p} isDark={isDark} />
            </div>
            {p.custom_headline && (
              <div className="text-base md:text-lg italic mb-2" style={{ color: theme.inkMuted }}>
                „{p.custom_headline}"
              </div>
            )}
            <h2
              className="text-2xl md:text-4xl lg:text-5xl font-bold leading-tight tracking-tight"
              style={{ color: theme.ink }}
            >
              {p.name}
            </h2>
            {p.price ? (
              <div
                className="mt-4 text-4xl md:text-5xl font-bold tracking-tight"
                style={{ color: theme.accent }}
              >
                {p.price} €
              </div>
            ) : (
              <div
                className="mt-4 inline-flex items-center gap-1.5 text-base md:text-lg font-medium"
                style={{ color: theme.accent }}
              >
                <Lock className="size-4" />
                Großhandelspreis nach Registrierung
              </div>
            )}
            <div
              className="mt-6 inline-flex items-center gap-2 text-base font-semibold rounded-xl px-5 py-3 self-start"
              style={{ background: theme.accent, color: "#fff" }}
            >
              {p.price ? "Jetzt ansehen" : "Jetzt freischalten"}
              <ExternalLink className="size-4" />
            </div>
          </div>
        </div>
      </a>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Stock-Status Pills
// ----------------------------------------------------------------------------

function StockPills({ p, isDark }: { p: PublicProduct; isDark: boolean }) {
  const pills: { label: string; tone: "warn" | "hot" | "ok" }[] = [];

  // Bestseller — wenn total_sales > 50 oder hoch
  if (p.total_sales && p.total_sales >= 50) {
    pills.push({ label: "Bestseller", tone: "hot" });
  }

  // Low stock pill (simple products)
  if (p.type === "simple" && p.stock_quantity != null && p.stock_quantity > 0) {
    const lowThreshold = p.low_stock_amount ?? 5;
    if (p.stock_quantity <= lowThreshold) {
      pills.push({ label: `Nur noch ${p.stock_quantity} da!`, tone: "warn" });
    }
  }

  // Variable — wenn wenig Varianten verfügbar
  if (p.type === "variable" && p.available_variations != null && p.total_variations != null) {
    if (p.total_variations > 0 && p.available_variations < p.total_variations / 3) {
      pills.push({ label: `Nur ${p.available_variations} Varianten verfügbar`, tone: "warn" });
    }
  }

  if (pills.length === 0) return null;

  return (
    <>
      {pills.map((pill, i) => {
        const colors =
          pill.tone === "warn"
            ? { bg: isDark ? "#7c2d12" : "#fff7ed", text: isDark ? "#fed7aa" : "#9a3412" }
            : pill.tone === "hot"
              ? { bg: isDark ? "#7f1d1d" : "#fef2f2", text: isDark ? "#fecaca" : "#991b1b" }
              : { bg: "#ecfdf5", text: "#065f46" };
        return (
          <span
            key={i}
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
            style={{ background: colors.bg, color: colors.text }}
          >
            {pill.label}
          </span>
        );
      })}
    </>
  );
}

// ----------------------------------------------------------------------------
// Discount-Pill (Promo-Code-Anzeige)
// ----------------------------------------------------------------------------

function DiscountPill({ p }: { p: PublicProduct }) {
  if (!p.discount_code) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md border border-rose-300 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{ background: "#fef2f2", color: "#9f1239" }}
      title={p.discount_hint || "Rabatt-Code im Checkout eingeben"}
    >
      🏷️ <code className="font-mono">{p.discount_code}</code>
      {p.discount_hint && (
        <span className="ml-0.5 font-normal italic normal-case">— {p.discount_hint}</span>
      )}
    </span>
  );
}

// ----------------------------------------------------------------------------
// Curated section
// ----------------------------------------------------------------------------

function CuratedSection({
  products,
  theme,
  isDark,
  onClick,
  isB2bLocked,
  activeCat,
  onClearCat,
  agentCode,
}: {
  products: PublicProduct[];
  theme: ThemeT;
  isDark: boolean;
  onClick: (id: string) => void;
  isB2bLocked: boolean;
  activeCat: string | null;
  onClearCat: () => void;
  agentCode: string;
}) {
  return (
    <section id="produkte" className="mx-auto max-w-6xl px-4 pb-16 md:pb-24 scroll-mt-20">
      {activeCat && (
        <div
          className="mb-4 flex items-center gap-2 rounded-xl px-3 py-2 text-sm"
          style={{ background: theme.accent + "12", color: theme.ink }}
        >
          <span>
            Gefiltert nach <strong>{activeCat}</strong>
          </span>
          <button
            onClick={onClearCat}
            className="ml-auto text-xs underline"
            style={{ color: theme.accent }}
          >
            Filter zurücksetzen
          </button>
        </div>
      )}
      {products.length === 0 && (
        <div className="text-center text-sm py-12" style={{ color: theme.inkMuted }}>
          {activeCat
            ? `Keine Produkte in „${activeCat}" gefunden.`
            : "Noch keine Produkte ausgewählt."}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
        {products.map((p) => (
          <ProductCard
            key={p.id}
            product={p}
            theme={theme}
            isDark={isDark}
            onClick={onClick}
            layout="tile"
            isB2bLocked={isB2bLocked}
            agentCode={agentCode}
          />
        ))}
      </div>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Full Shop Section: Search, Categories, Sort, Price-Filter
// ----------------------------------------------------------------------------

type SortKey = "default" | "name" | "price_asc" | "price_desc" | "bestseller";

function FullShopSection({
  data,
  theme,
  isDark,
  onClick,
  activeCat,
  setActiveCat,
}: {
  data: PublicPage;
  theme: ThemeT;
  isDark: boolean;
  onClick: (id: string) => void;
  activeCat: string | null;
  setActiveCat: (cat: string | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("bestseller");
  const [priceMax, setPriceMax] = useState<number | null>(null);

  const priceCap = useMemo(() => {
    let max = 0;
    for (const p of data.products) {
      const n = parseFloat(p.price || "0");
      if (Number.isFinite(n) && n > max) max = n;
    }
    return Math.ceil(max);
  }, [data.products]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = data.products.filter((p) => {
      if (activeCat && !(p.categories || []).includes(activeCat)) return false;
      if (q && !p.name.toLowerCase().includes(q)) return false;
      if (priceMax != null) {
        const n = parseFloat(p.price || "0");
        if (Number.isFinite(n) && n > priceMax) return false;
      }
      return true;
    });
    if (sort === "name") {
      list = [...list].sort((a, b) => a.name.localeCompare(b.name, "de"));
    } else if (sort === "price_asc") {
      list = [...list].sort((a, b) => parseFloat(a.price || "0") - parseFloat(b.price || "0"));
    } else if (sort === "price_desc") {
      list = [...list].sort((a, b) => parseFloat(b.price || "0") - parseFloat(a.price || "0"));
    } else if (sort === "bestseller") {
      list = [...list].sort((a, b) => (b.total_sales || 0) - (a.total_sales || 0));
    }
    return list;
  }, [data.products, query, activeCat, sort, priceMax]);

  return (
    <section id="produkte" className="mx-auto max-w-6xl px-4 pb-16 md:pb-24 scroll-mt-20">
      <div className="relative mb-3">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 size-4"
          style={{ color: theme.inkMuted }}
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Im Sortiment suchen…"
          className={`w-full rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 border ${
            isDark ? "border-white/10" : "border-black/10"
          }`}
          style={{ background: theme.cardBg, color: theme.ink }}
        />
      </div>

      {/* Sort + Price filter */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className={`rounded-xl px-3 py-2 text-sm border ${
            isDark ? "border-white/10" : "border-black/10"
          }`}
          style={{ background: theme.cardBg, color: theme.ink }}
        >
          <option value="bestseller">Sortierung: Bestseller</option>
          <option value="default">Standard-Reihenfolge</option>
          <option value="name">Name (A→Z)</option>
          <option value="price_asc">Preis aufsteigend</option>
          <option value="price_desc">Preis absteigend</option>
        </select>
        {priceCap > 0 && (
          <div
            className={`rounded-xl px-3 py-2 text-sm border flex items-center gap-2 ${
              isDark ? "border-white/10" : "border-black/10"
            }`}
            style={{ background: theme.cardBg, color: theme.inkMuted }}
          >
            <span className="text-xs flex-shrink-0">Max</span>
            <input
              type="range"
              min={1}
              max={priceCap}
              value={priceMax ?? priceCap}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setPriceMax(v >= priceCap ? null : v);
              }}
              className="flex-1 accent-orange-500"
            />
            <span className="text-xs font-mono flex-shrink-0">
              {(priceMax ?? priceCap).toFixed(0)} €
            </span>
          </div>
        )}
      </div>

      {data.categories.length > 0 && (
        <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1 -mx-1 px-1">
          <CategoryChip
            label={`Alle (${data.products.length})`}
            active={activeCat === null}
            onClick={() => setActiveCat(null)}
            theme={theme}
            isDark={isDark}
          />
          {data.categories.map((c) => {
            const count = data.products.filter((p) => (p.categories || []).includes(c)).length;
            return (
              <CategoryChip
                key={c}
                label={`${c} (${count})`}
                active={activeCat === c}
                onClick={() => setActiveCat(c)}
                theme={theme}
                isDark={isDark}
              />
            );
          })}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center text-sm py-12" style={{ color: theme.inkMuted }}>
          Keine Produkte gefunden.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {filtered.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              theme={theme}
              isDark={isDark}
              onClick={onClick}
              layout="tile"
              isB2bLocked={data.is_b2b_locked}
              agentCode={data.agent_code}
            />
          ))}
        </div>
      )}

      <div
        className="mt-6 text-xs text-center inline-flex items-center justify-center gap-1.5 w-full"
        style={{ color: theme.inkMuted }}
      >
        <span
          className="inline-block size-1.5 rounded-full animate-pulse"
          style={{ background: "#10b981" }}
        />
        <span>
          <strong className="font-semibold" style={{ color: theme.ink }}>
            {data.products.length}
          </strong>{" "}
          Produkte live · synchron mit dpconnect.de
          {filtered.length !== data.products.length && (
            <span> · {filtered.length} gefiltert</span>
          )}
        </span>
      </div>
    </section>
  );
}

function CategoryChip({
  label,
  active,
  onClick,
  theme,
  isDark,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  theme: ThemeT;
  isDark: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-medium border whitespace-nowrap transition ${
        active
          ? ""
          : isDark
            ? "border-white/10 hover:border-white/20"
            : "border-black/10 hover:border-black/20"
      }`}
      style={
        active
          ? { background: theme.accent, color: "#fff", borderColor: theme.accent }
          : { background: theme.cardBg, color: theme.inkMuted }
      }
    >
      {label}
    </button>
  );
}

function ProductCard({
  product: p,
  theme,
  isDark,
  onClick,
  layout,
  isB2bLocked,
  agentCode,
}: {
  product: PublicProduct;
  theme: ThemeT;
  isDark: boolean;
  onClick: (id: string) => void;
  layout: "row" | "tile";
  isB2bLocked?: boolean;
  agentCode?: string;
}) {
  const isVariable = p.type === "variable";
  // PDP-Link wenn nicht B2B-locked und wir eine wc_product_id haben
  const pdpHref = !isB2bLocked && p.wc_product_id ? `/p/${p.wc_product_id}` : p.permalink;
  const opensExternal = !pdpHref.startsWith("/");
  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClick(p.id);
    // SPA-navigation nur bei internem Link + normaler Click
    if (!opensExternal && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      navigate(pdpHref);
    }
  };
  const wishlistProduct = {
    wc_product_id: p.wc_product_id,
    name: p.name,
    image_url: p.image_url,
    price: p.price,
    permalink: p.permalink,
  };

  if (layout === "tile") {
    return (
      <a
        href={pdpHref}
        target={opensExternal ? "_blank" : undefined}
        rel={opensExternal ? "noopener noreferrer" : undefined}
        onClick={handleNav}
        onAuxClick={() => onClick(p.id)}
        className={`flex flex-col rounded-2xl p-3 md:p-4 transition-all duration-200 group border hover:-translate-y-1 ${
          isDark
            ? "border-white/10 hover:border-white/30 hover:shadow-2xl"
            : "border-black/5 hover:border-black/15 hover:shadow-xl"
        }`}
        style={{
          background: theme.cardBg,
          boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.2)" : "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <div className="relative overflow-hidden rounded-xl mb-3">
          {p.image_url ? (
            <img
              src={p.image_url}
              alt=""
              className="w-full aspect-square object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div
              className="w-full aspect-square"
              style={{ background: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.04)" }}
            />
          )}
          {agentCode && !isB2bLocked && p.wc_product_id > 0 && (
            <WishlistHeart agentCode={agentCode} product={wishlistProduct} theme={theme} />
          )}
          {isVariable && p.available_variations !== undefined && p.available_variations > 0 && (
            <span
              className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full backdrop-blur-sm font-medium"
              style={{ background: "rgba(255,255,255,.85)", color: theme.ink }}
            >
              {p.available_variations} Varianten
            </span>
          )}
        </div>
        <h3
          className="font-semibold text-sm md:text-base leading-tight line-clamp-2 min-h-[2.5rem] md:min-h-[3rem]"
          style={{ color: theme.ink }}
        >
          {p.name}
        </h3>
        <div className="mt-2 flex items-end justify-between gap-2">
          {p.price ? (
            <div
              className="text-lg md:text-xl font-bold tracking-tight"
              style={{ color: theme.accent }}
            >
              {p.price} €
            </div>
          ) : (
            <div
              className="text-xs md:text-sm font-semibold inline-flex items-center gap-1"
              style={{ color: theme.accent }}
            >
              <Lock className="size-3" />
              Preis nach Login
            </div>
          )}
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          <StockPills p={p} isDark={isDark} />
          <DiscountPill p={p} />
        </div>
      </a>
    );
  }

  // row layout (curated)
  return (
    <a
      href={pdpHref}
      target={opensExternal ? "_blank" : undefined}
      rel={opensExternal ? "noopener noreferrer" : undefined}
      onClick={handleNav}
      onAuxClick={() => onClick(p.id)}
      className={`block rounded-2xl p-4 transition-all group border ${
        isDark
          ? "border-white/10 hover:border-white/20 hover:shadow-lg"
          : "border-black/5 hover:border-black/10 hover:shadow-md"
      }`}
      style={{ background: theme.cardBg }}
    >
      <div className="flex gap-3 items-start">
        <div className="relative size-20 flex-shrink-0">
          {p.image_url ? (
            <img
              src={p.image_url}
              alt=""
              className="w-full h-full object-cover rounded-xl"
              loading="lazy"
            />
          ) : (
            <div
              className="w-full h-full rounded-xl"
              style={{ background: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.04)" }}
            />
          )}
          {agentCode && !isB2bLocked && p.wc_product_id > 0 && (
            <WishlistHeart agentCode={agentCode} product={wishlistProduct} theme={theme} size="sm" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {p.custom_headline && (
            <div className="text-xs font-medium mb-1 italic" style={{ color: theme.inkMuted }}>
              „{p.custom_headline}"
            </div>
          )}
          <h3 className="font-semibold text-base leading-tight" style={{ color: theme.ink }}>
            {p.name}
          </h3>
          {p.price ? (
            <div className="mt-1 text-lg font-semibold" style={{ color: theme.accent }}>
              {p.price} €
            </div>
          ) : (
            <div
              className="mt-1 text-xs font-medium inline-flex items-center gap-1"
              style={{ color: theme.accent }}
            >
              <Lock className="size-3" />
              Großhandelspreis nach Login
            </div>
          )}
          <div className="mt-1 flex flex-wrap gap-1">
            <StockPills p={p} isDark={isDark} />
            <DiscountPill p={p} />
          </div>
        </div>
        <ExternalLink
          className="size-4 flex-shrink-0 mt-1 opacity-50 group-hover:opacity-100"
          style={{ color: theme.inkMuted }}
        />
      </div>
    </a>
  );
}

// ============================================================
// Live-Search-Bar mit Autocomplete-Dropdown
// ============================================================

function LiveSearchBar({
  agentCode,
  theme,
  isDark,
  isB2bLocked,
}: {
  agentCode: string;
  theme: ThemeT;
  isDark: boolean;
  isB2bLocked: boolean;
}) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [focused, setFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 250ms debounce — vermeidet ein Request pro Tastendruck
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const { data, isFetching } = useQuery({
    queryKey: ["search", agentCode, debouncedQ],
    queryFn: () => searchPublicProducts(agentCode, debouncedQ, 8),
    enabled: debouncedQ.length >= 2,
    staleTime: 30_000,
  });

  // Outside-Click schließt Dropdown
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const showDropdown = focused && q.trim().length >= 2;
  const hits = data?.hits || [];

  return (
    <div ref={containerRef} className="relative">
      <div
        className="flex items-center gap-2 rounded-2xl px-4 py-2.5 border transition-shadow"
        style={{
          background: theme.cardBg,
          borderColor: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.10)",
          boxShadow: focused ? `0 0 0 3px ${theme.accent}22` : "none",
        }}
      >
        <Search className="size-4 flex-shrink-0" style={{ color: theme.inkMuted }} />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocused(true)}
          placeholder="Produkte durchsuchen…"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-current/50"
          style={{ color: theme.ink }}
          aria-label="Produkt-Suche"
        />
        {isFetching && q.trim().length >= 2 && (
          <Loader2 className="size-4 flex-shrink-0 animate-spin" style={{ color: theme.inkMuted }} />
        )}
        {q && (
          <button
            onClick={() => setQ("")}
            className="text-current/60 hover:text-current"
            aria-label="Suche leeren"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div
          className="absolute left-0 right-0 mt-2 rounded-2xl border overflow-hidden shadow-xl z-30 text-left"
          style={{
            background: theme.cardBg,
            borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.08)",
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          {hits.length === 0 && !isFetching && (
            <div className="px-4 py-6 text-center text-sm" style={{ color: theme.inkMuted }}>
              Keine Treffer für „{q}"
            </div>
          )}
          {hits.map((h) => (
            <SearchHitRow key={h.id} hit={h} theme={theme} isB2bLocked={isB2bLocked} />
          ))}
          {hits.length > 0 && (
            <div
              className="px-4 py-2 text-[11px] uppercase tracking-wide border-t"
              style={{
                color: theme.inkMuted,
                borderColor: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)",
              }}
            >
              {hits.length} Treffer
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SearchHitRow({
  hit,
  theme,
  isB2bLocked,
}: {
  hit: SearchHit;
  theme: ThemeT;
  isB2bLocked: boolean;
}) {
  return (
    <a
      href={hit.permalink}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 px-3 py-2 hover:opacity-90 transition border-b last:border-b-0"
      style={{
        borderColor: "rgba(0,0,0,.04)",
      }}
    >
      <div
        className="size-12 rounded-lg overflow-hidden flex-shrink-0 grid place-items-center"
        style={{ background: "rgba(0,0,0,.04)" }}
      >
        {hit.image_url ? (
          <img src={hit.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <Search className="size-4 opacity-30" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium leading-tight line-clamp-2" style={{ color: theme.ink }}>
          {hit.name}
        </div>
        {hit.price && !isB2bLocked ? (
          <div className="text-xs mt-0.5" style={{ color: theme.accent }}>
            {hit.price} €
          </div>
        ) : isB2bLocked ? (
          <div className="text-xs mt-0.5 inline-flex items-center gap-1" style={{ color: theme.inkMuted }}>
            <Lock className="size-3" />
            Preis nach Login
          </div>
        ) : null}
      </div>
      <ExternalLink className="size-4 opacity-40" style={{ color: theme.inkMuted }} />
    </a>
  );
}

// ============================================================
// Footer mit optionalen Legal-Links (Inherit / Custom / None)
// ============================================================

type LegalKind = "imprint" | "terms" | "privacy";

function PageFooter({
  data,
  theme,
  isDark,
  fullName,
  logoUrl,
}: {
  data: PublicPage;
  theme: ThemeT;
  isDark: boolean;
  fullName: string;
  logoUrl: string | null;
}) {
  const [modal, setModal] = useState<LegalKind | null>(null);
  const mode = (data.footer_mode || "inherit") as "inherit" | "custom" | "none";

  // Welche Texte sind im Custom-Mode tatsächlich gepflegt?
  const customAvailable: Record<LegalKind, boolean> = {
    imprint: !!data.legal_imprint,
    terms: !!data.legal_terms,
    privacy: !!data.legal_privacy,
  };

  const showLegal = mode !== "none";
  const showCustomLinks = mode === "custom";
  const showInheritLinks = mode === "inherit";

  return (
    <>
      <footer
        className="border-t mt-12"
        style={{
          borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.08)",
          background: isDark ? "rgba(0,0,0,.2)" : "rgba(255,255,255,.4)",
        }}
      >
        <div className="mx-auto max-w-5xl px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            {/* Brand-Seite */}
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <img src={logoUrl} alt={fullName} className="h-8 w-auto object-contain" />
              ) : null}
              <div>
                <div className="text-sm font-semibold" style={{ color: theme.ink }}>
                  {data.headline || fullName}
                </div>
                <div className="text-xs" style={{ color: theme.inkMuted }}>
                  Empfehlungen von {fullName}
                </div>
              </div>
            </div>

            {/* Legal-Links */}
            {showLegal && (
              <nav
                className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs"
                style={{ color: theme.inkMuted }}
              >
                {showCustomLinks && customAvailable.imprint && (
                  <button onClick={() => setModal("imprint")} className="hover:underline">
                    Impressum
                  </button>
                )}
                {showCustomLinks && customAvailable.terms && (
                  <button onClick={() => setModal("terms")} className="hover:underline">
                    AGB
                  </button>
                )}
                {showCustomLinks && customAvailable.privacy && (
                  <button onClick={() => setModal("privacy")} className="hover:underline">
                    Datenschutz
                  </button>
                )}
                {showInheritLinks && (
                  <>
                    <a
                      href="https://dpconnect.de/impressum/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      Impressum
                    </a>
                    <a
                      href="https://dpconnect.de/agb/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      AGB
                    </a>
                    <a
                      href="https://dpconnect.de/datenschutzerklaerung/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      Datenschutz
                    </a>
                  </>
                )}
              </nav>
            )}
          </div>

          <div
            className="mt-6 pt-4 text-center text-xs border-t"
            style={{
              color: theme.inkMuted,
              borderColor: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)",
            }}
          >
            <a
              href="https://dpconnect.de"
              className="hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Powered by DP Connect
            </a>
          </div>
        </div>
      </footer>

      {/* Legal-Modal — rendert custom Text */}
      {modal && (
        <LegalModal
          kind={modal}
          data={data}
          theme={theme}
          isDark={isDark}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

function LegalModal({
  kind,
  data,
  theme,
  isDark,
  onClose,
}: {
  kind: LegalKind;
  data: PublicPage;
  theme: ThemeT;
  isDark: boolean;
  onClose: () => void;
}) {
  const title = kind === "imprint" ? "Impressum" : kind === "terms" ? "AGB" : "Datenschutz";
  const content =
    kind === "imprint"
      ? data.legal_imprint
      : kind === "terms"
        ? data.legal_terms
        : data.legal_privacy;

  // ESC-Key zum Schließen
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // Body-Scroll lock
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 py-8"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-full overflow-y-auto rounded-2xl shadow-2xl"
        style={{ background: theme.cardBg, color: theme.ink }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="sticky top-0 flex items-center justify-between px-5 py-3 border-b"
          style={{
            background: theme.cardBg,
            borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.08)",
          }}
        >
          <h3 className="text-base font-semibold">{title}</h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-current/5"
            aria-label="Schließen"
          >
            <X className="size-5" />
          </button>
        </div>
        <div
          className="px-5 py-4 prose prose-sm max-w-none"
          style={{ color: theme.ink }}
        >
          {content ? renderMarkdown(content) : (
            <p style={{ color: theme.inkMuted }}>Kein Text hinterlegt.</p>
          )}
        </div>
      </div>
    </div>
  );
}
